import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CustomLabel from "../components/CustomLabel";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import PinAccessDialog from "../components/PinAccessDialog";
import { COLORS } from "../assets/theme";
import { IPageable, Params } from "../types/table/interfaces.type";
import { getAdminReports } from "../services/ReportsService";
import { getAllVenues } from "../services/VenueService";
import { getCachedPinAccess } from "../utils/pinAccessCache";

type AdminTab = "clients" | "finance" | "expenses" | "reports";

type ReportType = "expense" | "profit";

interface AdminReportRow {
  id: string;
  title?: string;
  description?: string;
  amount?: string | number;
  date?: string;
  venue_name?: string;
  venue_id?: string | number;
  type?: ReportType;
  note?: string;
  [key: string]: any;
}

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const parseDateValue = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) return null;
  const [, day, month, year, hour, minute, second] = match;
  const d = Number(day);
  const m = Number(month) - 1;
  const y = Number(year);
  const h = Number(hour || "0");
  const min = Number(minute || "0");
  const sec = Number(second || "0");
  const date = new Date(y, m, d, h, min, sec);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseAmountValue = (value?: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const cleaned = Number(value.replace(/[^0-9.-]+/g, ""));
    if (Number.isFinite(cleaned)) return cleaned;
  }
  return 0;
};

const normalizeReportType = (value?: unknown): ReportType => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "expense" ? "expense" : "profit";
};

const normalizeReportRow = (
  item: any,
  type: ReportType,
  index: number
): AdminReportRow => {
  const venueIdCandidate =
    item?.venue_id ??
    item?.venueId ??
    item?.venue?.id ??
    item?.venue?.venue_id ??
    item?.invoice?.venue_id ??
    item?.invoice?.venueId ??
    item?.invoice?.venue?.id ??
    item?.invoice?.venue?.venue_id;
  const venueName =
    pickNonEmptyString(
      item?.venue_name,
      item?.venueName,
      item?.venue?.name,
      item?.venue,
      item?.invoice?.venue_name,
      item?.invoice?.venue?.name
    ) || (venueIdCandidate ? `Venue ${venueIdCandidate}` : "");
  const amountCandidate =
    item?.amount ??
    item?.net_amount ??
    item?.total_amount ??
    item?.invoice?.net_amount ??
    item?.invoice?.total_amount ??
    item?.paid_amount ??
    item?.price;
  const dateValue =
    pickNonEmptyString(
      item?.date,
      item?.expense_date,
      item?.created_at,
      item?.createdAt,
      item?.invoice_date,
      item?.invoice?.invoice_date,
      item?.invoice?.created_at
    ) || "";
  const titleValue =
    pickNonEmptyString(
      item?.title,
      item?.name,
      item?.invoice_title,
      item?.invoice_number,
      item?.Invoice_number,
      item?.invoice?.invoice_number,
      item?.invoice?.Invoice_number,
      item?.client_name,
      item?.client?.name,
      item?.client?.full_name,
      item?.client?.fullName,
      item?.description
    ) || "";
  const descriptionValue =
    item?.description ||
    item?.notes ||
    item?.note ||
    item?.invoice?.notes ||
    "";
  const resolvedDescription =
    type === "profit" && !String(descriptionValue).trim()
      ? "Paid invoice"
      : descriptionValue;
  const idCandidate =
    item?.id ||
    item?._id ||
    item?.expense_id ||
    item?.expenseId ||
    item?.invoice_id ||
    item?.invoice?.id ||
    item?.invoice?.invoice_id;

  return {
    id: String(idCandidate || `${type}-${index}`),
    title: titleValue,
    description: resolvedDescription,
    amount: parseAmountValue(amountCandidate),
    date: dateValue,
    venue_name: venueName,
    venue_id:
      venueIdCandidate !== undefined && venueIdCandidate !== null
        ? String(venueIdCandidate)
        : undefined,
    type,
    note: item?.notes || item?.note || "",
  };
};

const sortByDate = (rows: AdminReportRow[]): AdminReportRow[] => {
  return [...rows].sort((a, b) => {
    const aDate = parseDateValue(a.date || "");
    const bDate = parseDateValue(b.date || "");
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return bDate.getTime() - aDate.getTime();
  });
};

const normalizeReportResponse = (response: any): AdminReportRow[] => {
  const expenses =
    response?.expenses || response?.data?.expenses || response?.data?.expense;
  const profits =
    response?.profits || response?.data?.profits || response?.data?.profit;

  if (Array.isArray(expenses) || Array.isArray(profits)) {
    const normalizedExpenses = (Array.isArray(expenses) ? expenses : []).map(
      (item: any, index: number) => normalizeReportRow(item, "expense", index)
    );
    const normalizedProfits = (Array.isArray(profits) ? profits : []).map(
      (item: any, index: number) => normalizeReportRow(item, "profit", index)
    );
    return sortByDate([...normalizedProfits, ...normalizedExpenses]);
  }

  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  if (!Array.isArray(list)) return [];

  return sortByDate(
    list.map((item: any, index: number) =>
      normalizeReportRow(item, normalizeReportType(item?.type), index)
    )
  );
};

const AdminReports: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "reports";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [reports, setReports] = useState<AdminReportRow[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportDateRange, setReportDateRange] = useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });
  const [venues, setVenues] = useState<{ id: string; name: string }[]>([]);
  const [venueFilter, setVenueFilter] = useState<string>("all");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  const handleFinanceNav = (route: string) => {
    if (getCachedPinAccess()) {
      navigate(route);
      return;
    }
    setPendingRoute(route);
    setPinDialogOpen(true);
  };

  const handlePinVerified = () => {
    if (pendingRoute) {
      navigate(pendingRoute);
    }
    setPendingRoute(null);
  };

  const handlePinClose = () => {
    setPinDialogOpen(false);
    setPendingRoute(null);
  };

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }),
    []
  );

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await getAllVenues();
        const mapped = (Array.isArray(response) ? response : [])
          .map((venue: any) => {
            const id =
              venue?.id ??
              venue?._id ??
              venue?.venue_id ??
              venue?.venueId ??
              venue?.user_id;
            if (!id) return null;
            const name =
              pickNonEmptyString(
                venue?.name,
                venue?.venue_name,
                venue?.title
              ) || `Venue ${id}`;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];
        setVenues(mapped);
      } catch (error) {
        console.error("Error fetching venues:", error);
        setVenues([]);
      }
    };
    fetchVenues();
  }, []);

  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;

      if (reportDateRange) {
        if (Array.isArray(reportDateRange)) {
          startDate = reportDateRange[0];
          endDate = reportDateRange[1];
        } else {
          startDate = reportDateRange.start;
          endDate = reportDateRange.end;
        }
      }

      const response = await getAdminReports({
        start_date: startDate,
        end_date: endDate,
      });
      setReports(normalizeReportResponse(response));
    } catch (error) {
      console.error("Error fetching admin reports:", error);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, [reportDateRange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();
    const venueNameById = new Map(venues.map((v) => [v.id, v.name]));
    const selectedVenueName =
      venueFilter !== "all" ? venueNameById.get(String(venueFilter)) : undefined;

    return reports.filter((row) => {
      if (reportDateRange) {
        const rowDate = parseDateValue(row.date || "");
        if (rowDate) {
          let startDate: Date | null = null;
          let endDate: Date | null = null;
          if (Array.isArray(reportDateRange)) {
            startDate = parseDateValue(reportDateRange[0] as any);
            endDate = parseDateValue(reportDateRange[1] as any);
          } else {
            startDate = parseDateValue((reportDateRange as any).start);
            endDate = parseDateValue((reportDateRange as any).end);
          }

          if (startDate && rowDate < startDate) return false;
          if (endDate && rowDate > endDate) return false;
        }
      }

      if (venueFilter && venueFilter !== "all") {
        const rowVenueId = row.venue_id ? String(row.venue_id) : "";
        if (rowVenueId) {
          if (rowVenueId !== String(venueFilter)) return false;
        } else if (selectedVenueName) {
          const rowVenueName = String(row.venue_name || "").toLowerCase();
          if (rowVenueName !== selectedVenueName.toLowerCase()) return false;
        } else {
          return false;
        }
      }

      if (!q) return true;

      const haystack = [
        row.title,
        row.description,
        row.amount,
        row.date,
        row.venue_name,
        row.type,
        row.note,
      ]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });
  }, [reports, reportDateRange, search, venueFilter, venues]);

  const totals = useMemo(() => {
    return filteredReports.reduce(
      (acc, row) => {
        const amount = Number(row.amount) || 0;
        if (row.type === "expense") {
          acc.expense += amount;
        } else {
          acc.profit += amount;
        }
        return acc;
      },
      { expense: 0, profit: 0 }
    );
  }, [filteredReports]);

  const netTotal = totals.profit - totals.expense;

  const fetchReportsTable = useCallback(
    async (params: Params<AdminReportRow>): Promise<IPageable<AdminReportRow>> => ({
      content: filteredReports,
      number: params.page || 0,
      size: filteredReports.length || 1,
      totalElements: filteredReports.length,
      totalPages: 1,
    }),
    [filteredReports]
  );

  const exportReportsCsv = useCallback(() => {
    if (!reports || reports.length === 0) return;
    const keys = [
      "type",
      "title",
      "description",
      "amount",
      "date",
      "venue_name",
      "note",
    ];
    const header = keys.join(",");
    const lines = reports.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reports-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [reports]);

  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    { key: "finance", label: "Invoices", icon: <AccountBalanceWalletOutlinedIcon /> },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        display: "flex",
        bgcolor: COLORS.MainBackground,
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <Box
        sx={{
          width: sidebarCollapsed ? 72 : 240,
          flexShrink: 0,
          borderRight: "1px solid #eee",
          bgcolor: COLORS.WhiteBackground,
          py: 2,
          px: sidebarCollapsed ? 1 : 1.5,
          transition: "width 0.2s ease",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" fontWeight={700} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "clients") {
                      navigate("/all-data");
                    } else if (item.key === "finance") {
                      handleFinanceNav("/all-data/invoices");
                    } else if (item.key === "expenses") {
                      handleFinanceNav("/all-data/expenses");
                    } else if (item.key === "reports") {
                      handleFinanceNav("/all-data/reports");
                    }
                  }}
                  sx={{
                    mb: 0.8,
                    mx: 0.2,
                    borderRadius: 2,
                    minHeight: 44,
                    px: sidebarCollapsed ? 1.4 : 2,
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                    color: selected ? COLORS.MainColor : "text.secondary",
                    bgcolor: selected
                      ? COLORS.SidebarActiveBg || "rgba(25,118,210,0.08)"
                      : "transparent",
                    "&:hover": {
                      bgcolor: selected
                        ? COLORS.SidebarActiveBg || "rgba(25,118,210,0.12)"
                        : "rgba(0,0,0,0.03)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 0.5, sm: 1 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <CustomLabel
          text={activeTabLabel}
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 1 }}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                ...venues.map((venue) => ({
                  label: venue.name,
                  value: venue.id,
                })),
              ],
              value: venueFilter,
              onChange: (v: any) => {
                const next =
                  v === null || v === undefined || v === "" ? "all" : String(v);
                setVenueFilter(next);
              },
              placeholder: "Venue",
              label: "Venue",
              size: "small",
              searchable: false,
              clearable: true,
              labelBgColor: COLORS.WhiteBackground,
              sx: { width: 200 },
            }}
            dateRangeConfig={{
              value: reportDateRange ?? undefined,
              onChange: (v: any) => setReportDateRange(v),
              fullWidth: false,
            }}
            buttonConfig={[
              {
                label: "Export CSV",
                onClick: () => exportReportsCsv(),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminReportRow>
              filters={{ page: 0, size: 10, search }}
              clientSidePagination
              fields={[
                {
                  key: "type",
                  header: "Type",
                  transform: (row) =>
                    row.type === "expense" ? "Expense" : "Profit",
                },
                { key: "title", header: "Title" },
                { key: "description", header: "Description" },
                {
                  key: "amount",
                  header: "Amount",
                  transform: (row) =>
                    `${row.type === "expense" ? "-" : ""}${currencyFormatter.format(
                      Math.abs(Number(row.amount) || 0)
                    )}`,
                },
                {
                  key: "date",
                  header: "Date",
                  transform: (row) => {
                    const d = new Date(row.date || "");
                    return Number.isNaN(d.getTime())
                      ? String(row.date || "")
                      : d.toLocaleDateString();
                  },
                },
                { key: "venue_name", header: "Venue" },
              ]}
              fetchFunc={fetchReportsTable}
              loading={loadingReports}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: COLORS.WhiteBackground,
              borderRadius: "0 0 10px 10px",
              padding: "16px",
              marginTop: "-10px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <CustomLabel
              text="Total Net Amount:"
              sx={{ color: COLORS.typographyColor }}
            />
            <CustomLabel
              text={currencyFormatter.format(netTotal)}
              fontWeight="bold"
              sx={{ color: COLORS.MainColor }}
            />
          </Box>
        </Box>
      </Box>
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={handlePinClose}
        onVerified={handlePinVerified}
      />
    </Box>
  );
};

export default AdminReports;
